import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useLenis } from "lenis/react";

/**
 * Global scroll-to-top on route change.
 * - Disables the browser's automatic scroll restoration so back/forward
 *   navigation also lands at the top.
 * - Uses Lenis (smooth scroll) when available with `immediate: true` so we
 *   don't see a smooth animated scroll on navigation, just an instant snap.
 * - Falls back to native window.scrollTo for safety on iOS/Android.
 * - Re-runs on pathname AND search changes so query-param navigation also resets.
 */
export function ScrollToTop() {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const lenis = useLenis();

  // Disable native scroll restoration once on mount (covers back/forward too).
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  useEffect(() => {
    // Use rAF to ensure the new route's DOM has mounted before we scroll.
    const raf = requestAnimationFrame(() => {
      if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true });
      }
      // Always also call native to cover edge cases (iOS Safari, lenis disabled).
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname, search, navigationType, lenis]);

  return null;
}
